import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import {
  loadCheckoutWebComponents,
  type Component,
  type Options,
} from '@checkout.com/checkout-web-components';
import { CREATE_SUBSCRIPTION_PAYMENT_SESSION } from '@monofront/business/queries';
import { ICreateSubscriptionPaymentSessionQueryResult } from '@monofront/types';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import FormContext from '@components/PaymentForms/FormContext';
import useI18n from '@hooks/i18n/useI18n';
import logger from '@utils/logger';
import { buildFlowConfig } from '../buildFlowConfig';

type CreateCardComponentArgs = {
  locale: string;
  createSession: () => Promise<{
    data?: ICreateSubscriptionPaymentSessionQueryResult | null;
  }>;
  onChange: (component: Component) => void;
  onError: Options['onError'];
};

const createCardComponent = async ({
  locale,
  createSession,
  onChange,
  onError,
}: CreateCardComponentArgs): Promise<Component> => {
  const { data } = await createSession();

  if (!data?.createSubscriptionPaymentSession) {
    throw new Error('Failed to create the card payment session');
  }

  const { id, paymentSessionToken } = data.createSubscriptionPaymentSession;

  const checkout = await loadCheckoutWebComponents(
    buildFlowConfig({
      borderRadius: ['8px', '9999px'],
      locale,
      paymentSession: { id, payment_session_token: paymentSessionToken },
      componentOptions: {},
      onError,
    }),
  );

  const card = checkout.create('card', {
    showPayButton: false,
    displayCardholderName: 'top',
    onChange,
    // The session only renders the fields: lego creates the payment from the
    // token, so Flow must stop after tokenizing.
    onTokenized: async () => ({ continue: false }),
  });

  if (!(await card.isAvailable())) {
    throw new Error(
      `Checkout.com card component is not available (payment session ${id})`,
    );
  }

  return card;
};

type CheckoutComFlowCardFieldsProps = {
  isLoading?: boolean;
  onComplete?: (isValid: boolean) => void;
  onSubmit: (cardToken: string) => void;
  children: React.ReactNode;
};

const CheckoutComFlowCardFields: React.FC<CheckoutComFlowCardFieldsProps> = ({
  isLoading,
  onComplete,
  onSubmit,
  children,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: 'onChange', criteriaMode: 'all' });
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<Component | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const tGlobal = useTranslations('Global');
  const { locale } = useI18n();
  const [createSubscriptionPaymentSession] =
    useMutation<ICreateSubscriptionPaymentSessionQueryResult>(
      CREATE_SUBSCRIPTION_PAYMENT_SESSION,
    );

  useEffect(() => {
    let cancelled = false;

    const notifyError = (error: unknown) => {
      logger.error('Flow card component error', { error });
      toast(tGlobal('errorOccurredTryAgain'), {
        progressClassName: 'toast-error',
      });
    };

    createCardComponent({
      locale,
      createSession: createSubscriptionPaymentSession,
      onChange: (component) => onComplete?.(component.isValid()),
      onError: (_component, error) => notifyError(error),
    })
      .then((card) => {
        // The modal can close during the init; a component created after the
        // cleanup ran is unreachable, so bin it instead of mounting it.
        if (cancelled || !containerRef.current) return card.unmount();

        cardRef.current = card;
        card.mount(containerRef.current);
      })
      .catch((error) => {
        if (cancelled) return;
        notifyError(error);
      })
      .finally(() => {
        if (!cancelled) setIsInitializing(false);
      });

    return () => {
      cancelled = true;
      cardRef.current?.unmount();
      cardRef.current = null;
    };
  }, []);

  const submitCard = async () => {
    try {
      const result = await cardRef.current?.tokenize();

      if (!result || result.type !== 'card') {
        throw new Error('Flow card tokenization returned no card token');
      }

      onSubmit(result.data.token);
    } catch (error) {
      logger.error('Flow card tokenization failed', { error });
      toast(tGlobal('errorOccurredTryAgain'), {
        progressClassName: 'toast-error',
      });
    }
  };

  return (
    <FormContext.Provider
      value={useMemo(
        () => ({ clearErrors, errors, isLoading, register, setError }),
        [clearErrors, errors, isLoading, register, setError],
      )}
    >
      {isInitializing && <HeartLoader />}
      <form onSubmit={handleSubmit(submitCard)}>
        <div data-testid="card-form-checkout" ref={containerRef} />
        {children}
      </form>
    </FormContext.Provider>
  );
};

export default CheckoutComFlowCardFields;
